import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppContext } from '../../app/provider/useAppContext';
import { appConfig } from '../../config/app-config';
import { resolveCartOnLogin } from '../../features/cart/api/cartApi';
import { CATEGORIES } from '../../shared/constants/api';
import * as api from '../../shared/services/apiClient';
import { AppContextType } from '../../shared/types/context';

import { AUTH_STORAGE_KEYS, AUTH_MESSAGES, AUTH_CONFIG } from './constants/auth.constants';
import * as loginService from './services/login';
import * as authService from './services/session';
import { TokenData } from './types/auth.types';

const PingCallback: React.FC = () => {
  const [status, setStatus] = useState<string>(AUTH_MESSAGES.PROCESSING_AUTH);
  const navigate = useNavigate();
  const [, setContext] = useAppContext();
  const hasRun = useRef<boolean>(false); // Prevent double execution in StrictMode

  useEffect(() => {
    // Prevent double execution
    if (hasRun.current) {
      // 
      return;
    }
    hasRun.current = true;

    const handleCallback = async () => {
      try {
        const tokenData: TokenData = await loginService.handleCallback();

        if (tokenData.access_token) {
          setStatus(AUTH_MESSAGES.AUTH_SUCCESS_REDIRECTING);

          // Update App Context with login info to refresh UI immediately
          if (setContext) {
            const userInfo = tokenData.userInfo || authService.getUserInfoSync();
            setContext((prev: AppContextType) => ({
              ...prev,
              loggedIn: true,
              accessToken: tokenData.access_token,
              userInfo: userInfo,
              authType: AUTH_CONFIG.PROVIDER_PING
            }));
          }

          // Extract store context from the intended redirect path to restore contextual boundaries
          const redirectPath = localStorage.getItem(AUTH_STORAGE_KEYS.POST_LOGIN_REDIRECT) || sessionStorage.getItem(AUTH_STORAGE_KEYS.POST_LOGIN_REDIRECT);
          let capturedStoreKey;
          if (redirectPath) {
            capturedStoreKey = redirectPath.split('/')[1];
            if (capturedStoreKey && capturedStoreKey !== 'undefined' && capturedStoreKey !== 'null') {
              api.setGlobalStoreKey(capturedStoreKey);
            }
          }

          // API client refresh no longer needed - using backend API

          // SINGLE AUTHORITY: Resolve cart on login
          // 
          const resolvedCart = await resolveCartOnLogin();

          if (resolvedCart) {
            // Emit cart update to notify other components
            if (setContext) {
              setContext((prev: AppContextType) => ({
                ...prev,
                cart: resolvedCart
              }));
            }
          }

          // Small delay to ensure cart is fully synced
          await new Promise(resolve => setTimeout(resolve, 500));

          // Set flag to indicate user just logged in (for checkout retry logic)
          sessionStorage.setItem(AUTH_STORAGE_KEYS.JUST_LOGGED_IN, 'true');

          // ---- REDIRECT LOGIC ----
          // We already retrieved redirectPath above, no need to read again.
          if (redirectPath) {
            localStorage.removeItem(AUTH_STORAGE_KEYS.POST_LOGIN_REDIRECT);
            sessionStorage.removeItem(AUTH_STORAGE_KEYS.POST_LOGIN_REDIRECT);
            navigate(redirectPath, { replace: true });
          } else {
            api.setGlobalStoreKey(appConfig.defaults.storeKey);
            // Fetch default event via backend API
            try {
              const categoriesRes = await api.get(CATEGORIES, {
                where: 'parent is not defined',
                limit: '1',
              });

              if (categoriesRes.ok) {
                const catData = await categoriesRes.json();
                const results = catData.results || catData;
                if (results.length > 0) {
                  const defaultEventKey = results[0].key;
                  navigate(`/${appConfig.defaults.storeKey}/${defaultEventKey}/checkout`, { replace: true });
                } else {
                  navigate(`/${appConfig.defaults.storeKey}/${appConfig.defaults.eventKey}/checkout`, { replace: true });
                }
              } else {
                navigate(`/${appConfig.defaults.storeKey}/${appConfig.defaults.eventKey}/checkout`, { replace: true });
              }
            } catch (_err) {
              // Final Fallback
              navigate(`/${appConfig.defaults.storeKey}/${appConfig.defaults.eventKey}/checkout`, { replace: true });
            }
          }
          // ---- FIX END ----
        }

      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        setStatus(`${AUTH_MESSAGES.AUTH_FAILED}: ${message}`);
      }
    };

    handleCallback();
  }, [navigate, setContext]);

  return (
    <div className="flex justify-center items-center h-screen flex-col font-sans bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-md text-center">
        <div className="text-lg mb-2.5">
          {status}
        </div>
        <div className="text-sm text-gray-500">
          {AUTH_MESSAGES.PLEASE_WAIT}
        </div>
      </div>
    </div>
  );
};

export default PingCallback;
